import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useWithdrawalTokens } from '../hooks'
import TokenListModal from '../components/TokenListModal'
import LidoWithdrawalForm from './LidoWithdrawalForm'
import TokenlonWithdrawalForm from './TokenlonWithdrawalForm'
import { stETH, wstETH } from '~/constants/tokens'
import {
  StyledWithdrawalStep,
  StyledStepNumber,
  StyledStepTitle,
} from './styled'
import { useGA } from '~/hooks'

interface WithdrawalSectionProps {
  isSelectedLido: boolean
  amount: string
  setAmount: (amount: string) => void
}

const WithdrawalSection: React.FC<WithdrawalSectionProps> = ({
  isSelectedLido,
  amount,
  setAmount,
}) => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const router = useRouter()
  const { token } = router.query

  const [isOpenTokenList, setIsOpenTokenList] = useState(false)

  const getInitialToken = (): BaseTokenlonToken => {
    if (token === 'stETH') return stETH
    if (token === 'wstETH') return wstETH
    return stETH
  }

  const [selectedToken, setSelectedToken] = useState<BaseTokenlonToken>(
    getInitialToken(),
  )

  useEffect(() => {
    const newToken = getInitialToken()
    if (newToken.address !== selectedToken.address) {
      setSelectedToken(newToken)
    }
  }, [token, selectedToken.address])

  const tokens = useWithdrawalTokens()

  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'lido_withdraw' },
  })
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // 最多输入 6 位小数
    if (value === '' || /^(\d+)(\.\d{0,6})?$/.test(value)) {
      setAmount(value)
    }
  }

  const handleBlur = () => {
    if (!amount) return
    // 去掉前导零
    let [int, dec] = amount.split('.')
    int = String(Number(int))
    setAmount(dec !== undefined ? `${int}.${dec}` : int)
  }

  const handleSelectToken = (token: BaseTokenlonToken) => {
    const trackId = isSelectedLido
      ? 'webtl_stk_withdraw_token'
      : 'webtl_stk_withdraw_taker'
    trackClick(trackId, {
      symbol: token.symbol,
    })

    setSelectedToken(token)
    setIsOpenTokenList(false)

    const tokenParam = token.address === stETH.address ? 'stETH' : 'wstETH'
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, token: tokenParam },
      },
      undefined,
      { shallow: true },
    )
  }

  return (
    <div>
      <StyledWithdrawalStep>
        <StyledStepNumber>1</StyledStepNumber>
        <StyledStepTitle>{t('withdraw')}</StyledStepTitle>
      </StyledWithdrawalStep>

      {isSelectedLido ? (
        <LidoWithdrawalForm
          amount={amount}
          setAmount={setAmount}
          getInitialToken={getInitialToken}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          setIsOpenTokenList={setIsOpenTokenList}
          toggleWalletModal={toggleWalletModal}
          toggleSwitchNetworkModal={toggleSwitchNetworkModal}
        />
      ) : (
        <TokenlonWithdrawalForm
          amount={amount}
          setAmount={setAmount}
          getInitialToken={getInitialToken}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          setIsOpenTokenList={setIsOpenTokenList}
          toggleWalletModal={toggleWalletModal}
          toggleSwitchNetworkModal={toggleSwitchNetworkModal}
        />
      )}
      <TokenListModal
        tokens={tokens}
        isOpen={isOpenTokenList}
        onDismiss={() => setIsOpenTokenList(false)}
        onSelect={handleSelectToken}
      />
    </div>
  )
}

export default WithdrawalSection
